import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import {
  AlertTriangle, Download, Grid3X3, LayoutList, RefreshCw, X,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AreaHealth {
  delivered: number;
  queued: number;
  blocked: number;
  total: number;
  health_tier: 'healthy' | 'at_risk' | 'early_stage';
  health_pct: number;
}

interface EpicSummary {
  id: string;
  title: string;
  status: string;
  wsjf: number;
  tier: string;
  is_blocked: boolean;
  escalation_count: number;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
}

interface BacklogArea {
  id: string;
  name: string;
  description: string;
  health: AreaHealth;
  epics: EpicSummary[];
}

interface AreaDetail {
  area: { id: string; name: string; description: string; health: AreaHealth };
  delivered: EpicSummary[];
  planned: EpicSummary[];
  meta: { delivered_total: number; planned_total: number };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ZPC_ID = 'marvis-labs';
const BASE = '/api/v1/zpc';

const STATUS_OPTIONS = ['todo', 'in_progress', 'done', 'blocked', 'deferred'];
const TIER_OPTIONS   = ['product', 'engineering', 'spike', 'hotfix'];

const STATUS_BADGE: Record<string, string> = {
  todo:        'bg-slate-700 text-slate-300',
  in_progress: 'bg-cyan-900 text-cyan-200',
  done:        'bg-emerald-900 text-emerald-200',
  blocked:     'bg-red-900 text-red-200',
  deferred:    'bg-yellow-900 text-yellow-200',
};

const HEALTH_CLASSES: Record<string, { border: string; bg: string; text: string }> = {
  healthy:     { border: 'border-teal-500', bg: 'bg-teal-950', text: 'text-teal-300' },
  at_risk:     { border: 'border-amber-500', bg: 'bg-amber-950', text: 'text-amber-300' },
  early_stage: { border: 'border-slate-600', bg: 'bg-slate-800', text: 'text-slate-400' },
};

// ── HealthBadge ───────────────────────────────────────────────────────────────

const HealthBadge: React.FC<{ health: AreaHealth; compact?: boolean }> = ({ health, compact }) => {
  const cls = HEALTH_CLASSES[health.health_tier] ?? HEALTH_CLASSES.early_stage;
  if (health.total === 0) return <span className="text-xs text-slate-500">No data</span>;
  const label = `${health.delivered}/${health.total}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono
        border ${cls.border} ${cls.bg} ${cls.text}`}
      aria-label={`${health.delivered} of ${health.total} delivered`}
    >
      {!compact && health.blocked > 0 && (
        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
      )}
      {label}
    </span>
  );
};

// ── FilterBar ─────────────────────────────────────────────────────────────────

interface Filters {
  status: string[];
  tier: string[];
  wsjfMin: string;
  wsjfMax: string;
}

const FilterBar: React.FC<{
  filters: Filters;
  onChange: (f: Filters) => void;
  onClear: () => void;
}> = ({ filters, onChange, onClear }) => {
  const toggle = (key: 'status' | 'tier', val: string) => {
    const cur = filters[key];
    onChange({ ...filters, [key]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] });
  };
  const hasFilters = filters.status.length > 0 || filters.tier.length > 0 ||
    filters.wsjfMin !== '' || filters.wsjfMax !== '';
  return (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-800/60 border border-slate-700 rounded-lg text-xs">
      <span className="text-slate-500 font-medium uppercase tracking-wide">Filter</span>
      <div className="flex items-center gap-1">
        <span className="text-slate-400 mr-1">Status</span>
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => toggle('status', s)}
            className={`px-2 py-0.5 rounded transition-colors ${filters.status.includes(s)
              ? 'bg-cyan-700 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-slate-400 mr-1">Tier</span>
        {TIER_OPTIONS.map(t => (
          <button key={t} onClick={() => toggle('tier', t)}
            className={`px-2 py-0.5 rounded transition-colors ${filters.tier.includes(t)
              ? 'bg-amber-700 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-slate-400">WSJF</span>
        <input type="number" placeholder="min" min={0} max={100} value={filters.wsjfMin}
          onChange={e => onChange({ ...filters, wsjfMin: e.target.value })}
          className="w-16 px-2 py-0.5 bg-slate-700 border border-slate-600 rounded text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500" />
        <span className="text-slate-500">–</span>
        <input type="number" placeholder="max" min={0} max={100} value={filters.wsjfMax}
          onChange={e => onChange({ ...filters, wsjfMax: e.target.value })}
          className="w-16 px-2 py-0.5 bg-slate-700 border border-slate-600 rounded text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500" />
      </div>
      {hasFilters && (
        <button onClick={onClear} className="text-slate-400 hover:text-slate-200 underline ml-1">
          Clear all
        </button>
      )}
    </div>
  );
};

// ── BacklogTable ──────────────────────────────────────────────────────────────

type SortField = 'area' | 'title' | 'status' | 'wsjf' | 'tier' | 'id';

const BacklogTable: React.FC<{
  areas: BacklogArea[];
  onAreaClick: (areaId: string) => void;
}> = ({ areas, onAreaClick }) => {
  const [sortField, setSortField] = useState<SortField>('wsjf');
  const [sortAsc, setSortAsc] = useState(false);

  const rows = areas.flatMap(a => a.epics.map(e => ({ ...e, areaId: a.id, areaName: a.name })));
  const sorted = [...rows].sort((a, b) => {
    const mul = sortAsc ? 1 : -1;
    if (sortField === 'wsjf') return mul * (a.wsjf - b.wsjf);
    if (sortField === 'area') return mul * a.areaName.localeCompare(b.areaName);
    if (sortField === 'title') return mul * a.title.localeCompare(b.title);
    if (sortField === 'status') return mul * a.status.localeCompare(b.status);
    if (sortField === 'tier') return mul * a.tier.localeCompare(b.tier);
    return mul * a.id.localeCompare(b.id);
  });

  const toggleSort = (f: SortField) => {
    if (sortField === f) setSortAsc(v => !v);
    else { setSortField(f); setSortAsc(false); }
  };

  const Th: React.FC<{ field: SortField; label: string; cls?: string }> = ({ field, label, cls }) => (
    <th
      scope="col"
      className={`px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-300 ${cls ?? ''}`}
      onClick={() => toggleSort(field)}
      aria-sort={sortField === field ? (sortAsc ? 'ascending' : 'descending') : 'none'}
    >
      {label} {sortField === field ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  if (rows.length === 0) return (
    <div className="py-12 text-center text-slate-400">No epics match current filters.</div>
  );

  return (
    <div className="overflow-x-auto" role="region" aria-label="Backlog table">
      <table className="w-full text-sm" aria-rowcount={sorted.length}>
        <thead>
          <tr className="border-b border-slate-700">
            <Th field="area"   label="Area" />
            <Th field="title"  label="Title" cls="min-w-[16rem]" />
            <Th field="status" label="Status" />
            <Th field="wsjf"   label="WSJF" />
            <Th field="tier"   label="Tier" />
            <Th field="id"     label="Epic ID" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {sorted.map((e, i) => (
            <tr key={e.id} className="hover:bg-slate-700/30 transition-colors" aria-rowindex={i + 1}>
              <td className="px-4 py-2.5">
                <button
                  onClick={() => onAreaClick(e.areaId)}
                  className="text-xs text-cyan-400 hover:underline truncate max-w-[10rem] block text-left"
                  title={`Open ${e.areaName} drawer`}
                >
                  {e.areaName}
                </button>
              </td>
              <td className="px-4 py-2.5 text-slate-100 max-w-sm">
                <span className="line-clamp-2">{e.title}</span>
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[e.status] ?? 'bg-slate-700 text-slate-300'}`}>
                  {e.status}
                </span>
                {e.is_blocked && (
                  <AlertTriangle className="w-3 h-3 text-amber-400 inline ml-1" aria-label="escalation" />
                )}
              </td>
              <td className="px-4 py-2.5 font-mono text-right tabular-nums text-amber-300">
                {e.wsjf.toFixed(1)}
              </td>
              <td className="px-4 py-2.5 text-slate-400 text-xs">{e.tier}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{e.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-slate-700 text-xs text-slate-500">
        {sorted.length} epic{sorted.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// ── AreaSchematic ─────────────────────────────────────────────────────────────

const AreaNode: React.FC<{
  area: BacklogArea;
  isSelected: boolean;
  onClick: () => void;
  nodeRef: (el: HTMLButtonElement | null) => void;
}> = ({ area, isSelected, onClick, nodeRef }) => {
  const cls = HEALTH_CLASSES[area.health.health_tier] ?? HEALTH_CLASSES.early_stage;
  return (
    <button
      ref={nodeRef}
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all
        text-center w-full h-20 gap-1 opacity-0
        ${isSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900' : ''}
        ${cls.border} ${cls.bg} hover:brightness-125`}
      aria-label={`${area.name}: ${area.health.delivered}/${area.health.total} delivered`}
      aria-pressed={isSelected}
    >
      <span className={`text-xs font-medium leading-tight line-clamp-2 ${cls.text}`}>
        {area.name}
      </span>
      <HealthBadge health={area.health} compact />
    </button>
  );
};

const AreaSchematic: React.FC<{
  areas: BacklogArea[];
  selectedAreaId: string | null;
  onAreaClick: (areaId: string) => void;
}> = ({ areas, selectedAreaId, onAreaClick }) => {
  const nodeRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const nodes = nodeRefs.current.filter(Boolean);
    if (!nodes.length) return;
    gsap.fromTo(
      nodes,
      { opacity: 0, scale: 0.85 },
      { opacity: 1, scale: 1, duration: 0.3, stagger: 0.05, ease: 'power2.out' },
    );
  }, [areas.length]);

  if (areas.length === 0) return (
    <div className="py-12 text-center text-slate-400">No areas to display. Check platform-areas.md.</div>
  );

  return (
    <div className="grid grid-cols-4 gap-3 p-2" role="list" aria-label="Platform areas spatial view">
      {areas.map((a, i) => (
        <div key={a.id} role="listitem">
          <AreaNode
            area={a}
            isSelected={selectedAreaId === a.id}
            onClick={() => onAreaClick(a.id)}
            nodeRef={el => { nodeRefs.current[i] = el; }}
          />
        </div>
      ))}
    </div>
  );
};

// ── AreaDrawer ────────────────────────────────────────────────────────────────

const AreaDrawer: React.FC<{
  areaId: string;
  onClose: () => void;
}> = ({ areaId, onClose }) => {
  const [detail, setDetail] = useState<AreaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError('');
    fetch(`${BASE}/${ZPC_ID}/backlog/area/${areaId}`, { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setDetail)
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [areaId]);

  useEffect(() => {
    if (drawerRef.current) {
      gsap.fromTo(drawerRef.current,
        { x: '100%' }, { x: '0%', duration: 0.3, ease: 'power2.out' });
    }
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const close = () => {
    if (drawerRef.current) {
      gsap.to(drawerRef.current, { x: '100%', duration: 0.2, ease: 'power2.in',
        onComplete: onClose });
    } else {
      onClose();
    }
  };

  const areaData = detail?.area;

  return (
    <div
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-heading"
      className="fixed right-0 top-0 h-full w-96 bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
        <h2 id="drawer-heading" ref={headingRef} tabIndex={-1}
          className="text-base font-semibold text-slate-100 truncate pr-2 outline-none">
          {areaData?.name ?? areaId}
        </h2>
        <button onClick={close} aria-label="Close drawer"
          className="p-1 hover:bg-slate-700 rounded transition-colors">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {areaData && (
        <div className="px-4 py-2 border-b border-slate-700 shrink-0">
          <HealthBadge health={areaData.health} />
          {areaData.description && (
            <p className="text-xs text-slate-400 mt-1">{areaData.description}</p>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading && <p className="text-slate-400 text-sm" role="status" aria-live="polite">Loading…</p>}
        {error && (
          <div role="alert" className="text-red-400 text-sm bg-red-950 p-3 rounded">
            Unable to load area detail. <button onClick={() => {
              setError(''); setLoading(true);
              fetch(`${BASE}/${ZPC_ID}/backlog/area/${areaId}`)
                .then(r => r.json()).then(setDetail).catch(e => setError(e.message))
                .finally(() => setLoading(false));
            }} className="underline ml-1">Retry</button>
          </div>
        )}
        {detail && (
          <>
            <section aria-label="Delivered epics">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Delivered ({detail.meta.delivered_total})
              </h3>
              {detail.delivered.length === 0
                ? <p className="text-xs text-slate-500">No completed epics yet.</p>
                : detail.delivered.map(e => (
                  <div key={e.id} className="flex justify-between items-start py-1.5 border-b border-slate-800 gap-2">
                    <span className="text-xs text-slate-200 line-clamp-2 flex-1">{e.title}</span>
                    <span className="font-mono text-xs text-slate-500 shrink-0">{e.id}</span>
                  </div>
                ))}
            </section>
            <section aria-label="Planned epics">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Planned ({detail.meta.planned_total})
              </h3>
              {detail.planned.length === 0
                ? <p className="text-xs text-slate-500">No planned epics.</p>
                : detail.planned.map(e => (
                  <div key={e.id} className="flex justify-between items-start py-1.5 border-b border-slate-800 gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-slate-200 line-clamp-2 block">{e.title}</span>
                      <span className="text-xs font-mono text-amber-400">{e.wsjf.toFixed(1)}</span>
                      {e.is_blocked && <AlertTriangle className="w-3 h-3 text-amber-400 inline ml-1" />}
                    </div>
                    <span className="font-mono text-xs text-slate-500 shrink-0">{e.id}</span>
                  </div>
                ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main view ─────────────────────────────────────────────────────────────────

const EMPTY_FILTERS: Filters = { status: [], tier: [], wsjfMin: '', wsjfMax: '' };

export const ProductBacklogRoomView: React.FC = () => {
  const [areas, setAreas] = useState<BacklogArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Filters>(() => {
    try { return JSON.parse(sessionStorage.getItem('backlog_filters') || 'null') ?? EMPTY_FILTERS; }
    catch { return EMPTY_FILTERS; }
  });
  const [viewMode, setViewMode] = useState<'list' | 'spatial'>(() =>
    (localStorage.getItem('backlog_view_mode') as 'list' | 'spatial') ?? 'list'
  );
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const buildParams = useCallback((f: Filters): URLSearchParams => {
    const p = new URLSearchParams();
    f.status.forEach(s => p.append('status', s));
    f.tier.forEach(t => p.append('tier', t));
    if (f.wsjfMin !== '') p.set('wsjf_min', f.wsjfMin);
    if (f.wsjfMax !== '') p.set('wsjf_max', f.wsjfMax);
    p.set('limit', '500');
    return p;
  }, []);

  const loadData = useCallback((f: Filters) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError('');
    fetch(`${BASE}/${ZPC_ID}/backlog?${buildParams(f)}`, { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then((d: { areas: BacklogArea[] }) => setAreas(d.areas ?? []))
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
  }, [buildParams]);

  useEffect(() => { loadData(filters); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.setItem('backlog_filters', JSON.stringify(filters));
    const timer = setTimeout(() => loadData(filters), 300);
    return () => clearTimeout(timer);
  }, [filters, loadData]);

  const toggleViewMode = () => {
    const next = viewMode === 'list' ? 'spatial' : 'list';
    setViewMode(next);
    localStorage.setItem('backlog_view_mode', next);
  };

  const exportCSV = () => {
    const headers = ['area_id', 'area_name', 'epic_id', 'title', 'status', 'wsjf', 'tier'];
    const rows = areas.flatMap(a =>
      a.epics.map(e => [a.id, a.name, e.id, `"${e.title.replace(/"/g, '""')}"`,
                        e.status, e.wsjf.toFixed(1), e.tier])
    );
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `backlog-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const totalEpics = areas.reduce((n, a) => n + a.epics.length, 0);
  const totalDelivered = areas.reduce((n, a) => n + a.health.delivered, 0);
  const totalBlocked  = areas.reduce((n, a) => n + a.health.blocked, 0);

  return (
    <div className="space-y-4 relative">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            Product Backlog
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {totalEpics} epics · {totalDelivered} delivered · {totalBlocked} escalation{totalBlocked !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => loadData(filters)}
            disabled={loading}
            className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Refresh backlog"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
            aria-label="Export to CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={toggleViewMode}
            className="flex items-center gap-1 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
            aria-label={`Switch to ${viewMode === 'list' ? 'spatial' : 'list'} view`}
            aria-pressed={viewMode === 'spatial'}
          >
            {viewMode === 'list'
              ? <><Grid3X3 className="w-3.5 h-3.5" /> Spatial</>
              : <><LayoutList className="w-3.5 h-3.5" /> List</>}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <FilterBar filters={filters} onChange={setFilters} onClear={() => setFilters(EMPTY_FILTERS)} />

      {/* Error banner */}
      {error && (
        <div role="alert" className="p-3 bg-red-950 border border-red-800 rounded-lg text-red-300 text-sm">
          {error} — <button onClick={() => loadData(filters)} className="underline">Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading && areas.length === 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg" aria-busy="true">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 border-b border-slate-700/50 animate-pulse bg-slate-700/30" />
          ))}
        </div>
      )}

      {/* Content */}
      {!loading && areas.length === 0 && !error && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-10 text-center">
          <p className="text-slate-400">No epics match current filters.</p>
          <button onClick={() => setFilters(EMPTY_FILTERS)} className="text-cyan-400 underline text-sm mt-2">
            Clear all filters
          </button>
        </div>
      )}

      {areas.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
          {viewMode === 'list'
            ? <BacklogTable areas={areas} onAreaClick={setSelectedAreaId} />
            : <AreaSchematic areas={areas} selectedAreaId={selectedAreaId} onAreaClick={setSelectedAreaId} />}
        </div>
      )}

      {/* Area drawer overlay */}
      {selectedAreaId && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            aria-hidden="true"
            onClick={() => setSelectedAreaId(null)}
          />
          <AreaDrawer areaId={selectedAreaId} onClose={() => setSelectedAreaId(null)} />
        </>
      )}
    </div>
  );
};
